import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { tAlertSourceState } from 'src/alerts/AlertCenter.types';
import {
  LEAD_AGENT_CAP_RAISE_NOTICE_KEY,
  isSameLeadAgentCapRaiseNotice,
  selectLeadAgentCapRaiseNotice,
} from 'src/alerts/LeadAgentCapRaiseNotice.types';
import type { tLeadAgentCapRaiseNotice } from 'src/alerts/LeadAgentCapRaiseNotice.types';
import {
  META_PRICING_NOTICE_KEYS_BY_PRIORITY,
  isSameMetaPricingNotice,
  selectMetaPricingNotice,
} from 'src/alerts/MetaPricingNotice.types';
import type { tMetaPricingNotice } from 'src/alerts/MetaPricingNotice.types';
import {
  hasUnparsedMetaTokenInvalidRow,
  isSameMetaTokenInvalidNotice,
  selectMetaTokenInvalidNotice,
} from 'src/alerts/MetaTokenInvalidNotice.types';
import type { tMetaTokenInvalidNotice } from 'src/alerts/MetaTokenInvalidNotice.types';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { ActiveLeadsQuotaBannerService } from 'src/components/ActiveLeadsQuotaBanner/ActiveLeadsQuotaBanner.service';
import { LeadAgentSellerPausedBannerService } from 'src/components/LeadAgentSellerPausedBanner/LeadAgentSellerPausedBanner.service';
import type { tLeadAgentSellerStatus } from 'src/components/LeadAgentSellerPausedBanner/LeadAgentSellerPausedBanner.types';
import { GoogleCalendarConnectionService } from 'src/pages/SmartLeadbotConfig/tabs/Agenda/components/GoogleCalendarConnection/googleCalendarConnection.service';
import { CALENDAR_CONNECTION_EVENTS } from 'src/providers/NotificationProvider/Notification.events';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { fetchWorkspaceNotices } from './AlertCenter.service';
import type { tAlertSourceOptions } from './AlertCenter.types';

/**
 * The three fetch-backed alert sources, ported from the banners that own them
 * today. Each keeps the ORIGINAL request semantics — abort, focus refetch,
 * single-flight — because those are the behaviours that make the alert clear
 * itself without a reload; a naive re-fetch would look identical in a snapshot
 * test and be wrong in the tab.
 *
 * R3b — every source resolves to `loading` / `ready` / `error`. `error` is
 * resolved-unknown: it stops holding evaluation rather than pinning the
 * incumbent alert forever.
 */

const RESOLVED_NO_SELLER_STATUS: tAlertSourceState<tLeadAgentSellerStatus> = {
  status: 'ready',
  value: null,
};
/** Disabled/off-surface resolves "not revoked", never "unknown" (R3b). */
const RESOLVED_NOT_REVOKED: tAlertSourceState<boolean> = {
  status: 'ready',
  value: false,
};

/**
 * Keeps the previous state OBJECT when the resolved status/value are
 * unchanged. A focus refetch that returns the same answer must not mint a new
 * reference, or every consumer memoized on the snapshot re-renders on a
 * no-op poll.
 */
function nextSourceState<T>(
  previous: tAlertSourceState<T>,
  next: tAlertSourceState<T>,
  isSameValue: (a: T | null, b: T | null) => boolean = Object.is,
): tAlertSourceState<T> {
  if (
    previous.status === next.status &&
    isSameValue(previous.value, next.value)
  ) {
    return previous;
  }
  return next;
}

/**
 * The service mints a fresh object on every poll, so identity comparison would
 * publish a new reference on each focus refetch and re-render every consumer
 * memoized on the snapshot even when nothing changed.
 */
function isSameSellerStatus(
  a: tLeadAgentSellerStatus | null,
  b: tLeadAgentSellerStatus | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.isPaused === b.isPaused &&
    a.ownerName === b.ownerName &&
    a.pauseReason === b.pauseReason
  );
}

/**
 * Seller-facing "the bot is paused" signal.
 * GET workspace/{wid}/lead-agent/seller-status (bff-web).
 *
 * Ported from LeadAgentSellerPausedBanner: abort the in-flight request before
 * issuing a new one (a slow earlier response must never overwrite a newer one
 * on rapid focus events), and refetch on window focus so the strip disappears
 * once the owner reactivates the bot — without polling.
 *
 * The whole parsed status is carried, not just the boolean: the copy names the
 * owner to escalate to, and the service already refuses to report a pause it
 * cannot attribute (`isPaused && ownerName`), so the name is never absent when
 * the alert is live.
 */
export function useSellerStatusSource(
  options: tAlertSourceOptions,
): tAlertSourceState<tLeadAgentSellerStatus> {
  const { workspaceId, enabled, refetchToken = 0 } = options;
  const isEnabled = enabled && !!workspaceId;

  const [state, setState] = useState<tAlertSourceState<tLeadAgentSellerStatus>>(
    () =>
      isEnabled
        ? { status: 'loading', value: null }
        : RESOLVED_NO_SELLER_STATUS,
  );

  useEffect(() => {
    if (!isEnabled || !workspaceId) {
      setState((previous) =>
        nextSourceState(previous, RESOLVED_NO_SELLER_STATUS),
      );
      return undefined;
    }

    let active = true;
    let pendingController: AbortController | null = null;
    setState((previous) =>
      nextSourceState(previous, { status: 'loading', value: null }),
    );

    const fetchStatus = () => {
      pendingController?.abort();
      pendingController = new AbortController();
      LeadAgentSellerPausedBannerService.getSellerStatus(
        workspaceId,
        pendingController.signal,
      )
        .then((result) => {
          if (!active) return;
          setState((previous) =>
            nextSourceState(
              previous,
              { status: 'ready', value: result ?? null },
              isSameSellerStatus,
            ),
          );
        })
        .catch((e: unknown) => {
          // axios.isCancel matches the CanceledError thrown on an aborted
          // signal (cleanup / superseded fetch), not a real failure.
          if (axios.isCancel(e)) return;
          // F7 — LLog like the rest of the feature (AlertCenter.service), so
          // the failure reaches the same pipeline instead of dying in console.
          LLog.error(
            {
              component: LOG_COMPONENTS.NOTIFICATION,
              method: 'AlertCenter.sources',
              operation: LOG_OPERATIONS.ERROR,
            },
            'No se pudo consultar el estado del asesor (seller-status)',
            e as Error,
          );
          if (!active) return;
          setState((previous) =>
            nextSourceState(previous, { status: 'error', value: null }),
          );
        });
    };

    fetchStatus();

    const onFocus = () => fetchStatus();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      pendingController?.abort();
      window.removeEventListener('focus', onFocus);
    };
  }, [isEnabled, workspaceId, refetchToken]);

  return state;
}

/**
 * AGI-352 — whether the workspace's calendar connection is revoked, for the
 * `calendar-revoked` alert. POST workspace/{wid}/lead-agent/agenda/connection/sync
 * (bff-web), the SAME reconciling read the Agenda tab's card asks — one source
 * of truth, not a second definition of "connected". The endpoint is idempotent
 * (its own comment: asking twice about the same credential changes nothing),
 * and it resolves 424 to null in environments without the scheduling service,
 * which lands here as ready/null — no alert, no error noise.
 *
 * Two refresh paths, both of the refetch-don't-branch shape:
 * - the `calendar.connection.*` socket notifications (AGI-306) re-ask the
 *   backend instead of trusting the event's own payload;
 * - window focus, so the alert clears when the owner reconnects in the config
 *   page (a different surface, so this hook's `enabled` re-fetch also covers
 *   the SPA path back).
 */
export function useCalendarConnectionSource(
  options: tAlertSourceOptions,
): tAlertSourceState<boolean> {
  const { workspaceId, enabled, refetchToken = 0 } = options;
  const { newNotificationEvent } = useSocketsContext();
  const isEnabled = enabled && !!workspaceId;

  const [state, setState] = useState<tAlertSourceState<boolean>>(() =>
    isEnabled ? { status: 'loading', value: null } : RESOLVED_NOT_REVOKED,
  );

  // The service takes no abort signal, so staleness is guarded by sequence:
  // only the latest issued fetch may publish. A slow first answer must not
  // overwrite the refetch a revocation event just triggered.
  const fetchSeqRef = useRef(0);
  // The socket subscription must not be re-created when `enabled` flips —
  // read the volatile flag through a ref (same shape as the quota source).
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;

  const fetchConnection = useCallback(() => {
    const wid = workspaceIdRef.current;
    if (!isEnabledRef.current || !wid) return;
    fetchSeqRef.current += 1;
    const seq = fetchSeqRef.current;
    GoogleCalendarConnectionService.sync(wid)
      .then((connection) => {
        if (seq !== fetchSeqRef.current) return;
        setState((previous) =>
          nextSourceState(previous, {
            status: 'ready',
            // `null` (no scheduling service / no record) and every non-revoked
            // state resolve FALSE: only "Google stopped honouring the
            // credential" is the condition the alert exists for.
            value: connection?.state === 'revoked',
          }),
        );
      })
      .catch((e: unknown) => {
        if (axios.isCancel(e)) return;
        if (seq !== fetchSeqRef.current) return;
        // F7 — same pipeline as the rest of the feature, not console.
        LLog.error(
          {
            component: LOG_COMPONENTS.NOTIFICATION,
            method: 'AlertCenter.sources',
            operation: LOG_OPERATIONS.ERROR,
          },
          'No se pudo consultar el estado de la conexión de calendario',
          e as Error,
        );
        setState((previous) =>
          nextSourceState(previous, { status: 'error', value: null }),
        );
      });
  }, []);

  useEffect(() => {
    if (!isEnabled || !workspaceId) {
      // Invalidate anything in flight so it cannot publish after disable.
      fetchSeqRef.current += 1;
      setState((previous) => nextSourceState(previous, RESOLVED_NOT_REVOKED));
      return undefined;
    }

    setState((previous) =>
      nextSourceState(previous, { status: 'loading', value: null }),
    );
    fetchConnection();

    const onFocus = () => fetchConnection();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [isEnabled, workspaceId, refetchToken, fetchConnection]);

  // A revocation (or restoration) announces itself as a notification; the
  // event is the trigger, the sync endpoint is the truth (the relay has a
  // record of dropping payload fields — refetch, don't branch).
  newNotificationEvent.useSubscription((notification) => {
    if (!CALENDAR_CONNECTION_EVENTS.includes(notification?.event ?? '')) return;
    fetchConnection();
  });

  return state;
}

/**
 * Workspace-wide count of conversations hidden by the active-leads quota.
 * GET /workspace/{wid}/leads/quota-exceeded-count (bff-web proxy).
 *
 * Ported from ActiveLeadsQuotaBanner, including the single-flight guard with a
 * SINGLE trailing refetch: quota socket events arriving mid-flight must not
 * stack requests, but the in-flight response may predate the event, so exactly
 * one refetch runs after it settles. A failure keeps the last known count
 * (R8) rather than flashing it away; leaving the at-limit tier resets it so a
 * later episode never shows a previous episode's number.
 */
export function useQuotaExceededCountSource(
  options: tAlertSourceOptions,
): number | null {
  const { workspaceId, enabled, refetchToken = 0 } = options;
  const { billingQuotaUsageUpdated } = useSocketsContext();

  const [count, setCount] = useState<number | null>(null);
  const isFetchingRef = useRef(false);
  const pendingRefetchRef = useRef(false);
  // The socket subscription must not be re-created when `enabled` flips —
  // read the volatile flag through a ref instead of a dependency array.
  const isEnabledRef = useRef(enabled);
  isEnabledRef.current = enabled;

  const fetchCount = useCallback(async () => {
    if (!workspaceId) return;
    if (isFetchingRef.current) {
      pendingRefetchRef.current = true;
      return;
    }
    isFetchingRef.current = true;
    try {
      const response =
        await ActiveLeadsQuotaBannerService.getQuotaExceededCount(workspaceId);
      const value = response?.data?.quotaExceededCount;
      // Sparse-payload defense: only replace on a well-formed value.
      if (typeof value === 'number' && Number.isFinite(value)) {
        setCount(value);
      }
    } catch {
      // R8: count unavailable — keep the last known value (or null on first
      // load); the alert renders the count-less sentence.
    } finally {
      isFetchingRef.current = false;
      if (pendingRefetchRef.current) {
        pendingRefetchRef.current = false;
        fetchCount();
      }
    }
  }, [workspaceId]);

  useEffect(() => {
    if (enabled) {
      fetchCount();
      return;
    }
    setCount(null);
  }, [enabled, fetchCount, refetchToken]);

  billingQuotaUsageUpdated.useSubscription(() => {
    if (isEnabledRef.current) fetchCount();
  });

  return count;
}

/**
 * AGI-428: the workspace's live Meta-pricing notice, or `null`.
 * GET workspace/{wid}/notices (bff-web proxy over workspace-service).
 *
 * One source for both messages: message 2 (the video, 25-sep) wins over
 * message 1 while it is live, so every consumer keeps reading ONE notice and
 * tells the two apart by its key (`getMetaPricingMessage`).
 *
 * Same shape as its two fetch-backed siblings: `loading | ready | error`,
 * abort on unmount and before a superseding fetch, refetch on focus, and no
 * new reference when the answer is unchanged.
 *
 * Three things are specific to this source:
 *
 *  - A MALFORMED PAYLOAD IS ABSENCE, NOT AN ERROR. The row is seeded from a
 *    CSV by a one-shot command, so the realistic failure is a bad column, not
 *    a bad network. `error` would hold R3b's evaluation open forever on a row
 *    that is never going to parse; `ready`/`null` means "no notice for this
 *    workspace", which is the truth and lets every other alert proceed. The
 *    warning is what tells us to go fix the row.
 *  - THE CLIENT RE-CHECKS THE EXPIRY. The backend filters on `expires_at`,
 *    but a tab parked across the 5-oct cut-off would keep the last answer;
 *    `selectMetaPricingNotice` drops it (see the note there on why an
 *    unparseable date is NOT treated as expired).
 *  - THE CLOCK IS READ PER FETCH, not per render, so the expiry decision
 *    belongs to the response that carried the row.
 */
export function useWorkspaceNoticesSource(
  options: tAlertSourceOptions,
): tAlertSourceState<tMetaPricingNotice | null> {
  return useWorkspaceNoticeSource(options, META_PRICING_NOTICE_SOURCE);
}

/**
 * RET-2191: the workspace's live Lead Agent cap-raise notice, or `null`.
 * Same endpoint, same rules as the Meta notice above; only the row key and
 * the payload guard differ.
 *
 * It issues its OWN request rather than sharing the Meta source's: the two
 * notices have different audiences and lifetimes, and splitting one response
 * into two source states would change the Meta source's shape for its four
 * consumers (context, bell, auto-open host, overrides). The cost is one extra
 * small GET per load and per focus, only for owners and admins.
 */
export function useLeadAgentCapRaiseNoticeSource(
  options: tAlertSourceOptions,
): tAlertSourceState<tLeadAgentCapRaiseNotice | null> {
  return useWorkspaceNoticeSource(options, LEAD_AGENT_CAP_RAISE_NOTICE_SOURCE);
}

type tWorkspaceNoticeSourceSpec<T> = {
  /**
   * The `notice_key`s this source reads, in the order they win (the selector
   * applies the same order). Rows with any other key are not its business.
   */
  noticeKeys: readonly string[];
  select: (data: unknown, now: number) => T | null;
  isSame: (a: T | null, b: T | null) => boolean;
  /** Console line for a row with OUR key that did not parse. */
  invalidPayloadMessage: string;
};

const META_PRICING_NOTICE_SOURCE: tWorkspaceNoticeSourceSpec<tMetaPricingNotice> = {
  // AGI-428: message 2 (the video) wins over message 1 while it is live.
  noticeKeys: META_PRICING_NOTICE_KEYS_BY_PRIORITY,
  select: selectMetaPricingNotice,
  isSame: isSameMetaPricingNotice,
  invalidPayloadMessage:
    'El aviso de precios de Meta llego con un payload que no se pudo validar; se trata como ausente',
};

const LEAD_AGENT_CAP_RAISE_NOTICE_SOURCE: tWorkspaceNoticeSourceSpec<tLeadAgentCapRaiseNotice> = {
  noticeKeys: [LEAD_AGENT_CAP_RAISE_NOTICE_KEY],
  select: selectLeadAgentCapRaiseNotice,
  isSame: isSameLeadAgentCapRaiseNotice,
  invalidPayloadMessage:
    'El aviso de la subida de caps del Lead Agent llego con un payload que no se pudo validar; se trata como ausente',
};

/**
 * Whether the response carried a row with one of our keys that the selector
 * refused. Only THAT is worth a warning: since RET-2191 the endpoint serves
 * more than one notice, so "rows came back and mine is null" is also the
 * normal answer for a workspace that only has the other notice.
 *
 * With several keys in priority order, "refused" means the MOST PREFERRED key
 * present in the response is not the one that was selected. That still warns
 * when a message 2 row fails the guard and the selector falls back to message
 * 1, which is the case where nothing on screen would say anything is wrong.
 */
function hasUnparsedRow(
  data: unknown,
  noticeKeys: readonly string[],
  selectedKey: string | null,
): boolean {
  if (!Array.isArray(data)) return false;
  const keysInResponse = new Set(
    data.map((row) =>
      row && typeof row === 'object'
        ? (row as Record<string, unknown>).key
        : undefined,
    ),
  );
  const preferred = noticeKeys.find((key) => keysInResponse.has(key));
  return preferred !== undefined && preferred !== selectedKey;
}

function useWorkspaceNoticeSource<T extends { key: string }>(
  options: tAlertSourceOptions,
  spec: tWorkspaceNoticeSourceSpec<T>,
): tAlertSourceState<T | null> {
  const { workspaceId, enabled, refetchToken = 0 } = options;
  const isEnabled = enabled && !!workspaceId;
  // The spec objects are module constants; the ref keeps the effect's
  // dependency list honest without refetching when a caller passes a new one.
  const specRef = useRef(spec);
  specRef.current = spec;

  // Disabled resolves "this workspace has no notice", never "unknown" (R3b).
  const [state, setState] = useState<tAlertSourceState<T | null>>(() =>
    isEnabled ? { status: 'loading', value: null } : { status: 'ready', value: null },
  );

  useEffect(() => {
    const resolvedNone: tAlertSourceState<T | null> = { status: 'ready', value: null };
    if (!isEnabled || !workspaceId) {
      setState((previous) => nextSourceState(previous, resolvedNone));
      return undefined;
    }

    let active = true;
    let pendingController: AbortController | null = null;
    setState((previous) =>
      nextSourceState(previous, { status: 'loading', value: null }),
    );

    const fetchNotices = () => {
      pendingController?.abort();
      pendingController = new AbortController();
      fetchWorkspaceNotices(workspaceId, pendingController.signal)
        .then((response) => {
          if (!active) return;
          const { noticeKeys, select, isSame, invalidPayloadMessage } = specRef.current;
          const notice = select(response?.data, Date.now());
          // A row with our key that did not parse is the case worth a line in
          // the console: the endpoint answered, so this is OUR data being
          // wrong, and nothing else in the app will say so.
          if (hasUnparsedRow(response?.data, noticeKeys, notice?.key ?? null)) {
            LLog.warn(
              {
                component: LOG_COMPONENTS.NOTIFICATION,
                method: 'AlertCenter.sources',
                operation: LOG_OPERATIONS.RESPONSE,
              },
              invalidPayloadMessage,
              { data: response?.data as unknown },
            );
          }
          setState((previous) =>
            // Explicit type argument: the comparator's parameters are
            // `T | null`, which would otherwise infer `T` as the non-null form
            // and reject the state's own nullable value.
            nextSourceState<T | null>(
              previous,
              { status: 'ready', value: notice },
              isSame,
            ),
          );
        })
        .catch((e: unknown) => {
          if (axios.isCancel(e)) return;
          LLog.error(
            {
              component: LOG_COMPONENTS.NOTIFICATION,
              method: 'AlertCenter.sources',
              operation: LOG_OPERATIONS.ERROR,
            },
            'No se pudieron consultar los avisos del workspace (notices)',
            e as Error,
          );
          if (!active) return;
          setState((previous) =>
            nextSourceState(previous, { status: 'error', value: null }),
          );
        });
    };

    fetchNotices();

    const onFocus = () => fetchNotices();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      pendingController?.abort();
      window.removeEventListener('focus', onFocus);
    };
  }, [isEnabled, workspaceId, refetchToken]);

  return state;
}

/**
 * INT-1522: the workspace's live "Meta token invalid" notice, or `null`.
 * GET workspace/{wid}/notices (bff-web proxy over workspace-service) -- the
 * SAME endpoint useWorkspaceNoticesSource reads, filtered by a different
 * notice_key. A separate hook (its own fetch) rather than a second selector
 * over one shared call, matching this file's own pattern: every source here
 * is independently gated and independently fetched, never a shared cache
 * multiple concerns read from.
 *
 * NOT built on useWorkspaceNoticeSource/tWorkspaceNoticeSourceSpec (unlike
 * its pricing/cap-raise siblings above): that machinery's `noticeKeys` is a
 * finite, enumerable list of exact keys, which fits a fixed set of message
 * variants but not this notice's keying -- notice_key embeds a hash of
 * meta_account_id (see MetaMessengerHealthChangedListener), an open-ended,
 * per-workspace set the front cannot enumerate. Matching by prefix instead
 * (isMetaTokenInvalidNoticeKey) is a genuinely different selection strategy,
 * not a variant of the shared one, so this stays a standalone fetch rather
 * than forcing a prefix matcher into a spec shape built for exact lists.
 */
export function useMetaTokenInvalidNoticeSource(
  options: tAlertSourceOptions,
): tAlertSourceState<tMetaTokenInvalidNotice | null> {
  const { workspaceId, enabled, refetchToken = 0 } = options;
  const isEnabled = enabled && !!workspaceId;

  const [state, setState] = useState<tAlertSourceState<tMetaTokenInvalidNotice | null>>(() =>
    isEnabled ? { status: 'loading', value: null } : { status: 'ready', value: null },
  );

  useEffect(() => {
    if (!isEnabled || !workspaceId) {
      setState((previous) => nextSourceState(previous, { status: 'ready', value: null }));
      return undefined;
    }

    let active = true;
    let pendingController: AbortController | null = null;
    setState((previous) =>
      nextSourceState(previous, { status: 'loading', value: null }),
    );

    const fetchNotices = () => {
      pendingController?.abort();
      pendingController = new AbortController();
      fetchWorkspaceNotices(workspaceId, pendingController.signal)
        .then((response) => {
          if (!active) return;
          const notice = selectMetaTokenInvalidNotice(response?.data, Date.now());

          // A row under our prefix that did not parse is worth a console
          // line: the endpoint answered, so this is OUR data being wrong,
          // and (unlike the fixed-key sources) there is no "preferred key"
          // to compare against -- every account's row matters on its own.
          if (hasUnparsedMetaTokenInvalidRow(response?.data)) {
            LLog.warn(
              {
                component: LOG_COMPONENTS.NOTIFICATION,
                method: 'AlertCenter.sources',
                operation: LOG_OPERATIONS.RESPONSE,
              },
              'El aviso de token invalido de Meta llego con un payload que no se pudo validar; se trata como ausente',
              { data: response?.data as unknown },
            );
          }

          setState((previous) =>
            nextSourceState<tMetaTokenInvalidNotice | null>(
              previous,
              { status: 'ready', value: notice },
              isSameMetaTokenInvalidNotice,
            ),
          );
        })
        .catch((e: unknown) => {
          if (axios.isCancel(e)) return;
          LLog.error(
            {
              component: LOG_COMPONENTS.NOTIFICATION,
              method: 'AlertCenter.sources',
              operation: LOG_OPERATIONS.ERROR,
            },
            'No se pudieron consultar los avisos del workspace (meta token invalid notice)',
            e as Error,
          );
          if (!active) return;
          setState((previous) =>
            nextSourceState(previous, { status: 'error', value: null }),
          );
        });
    };

    fetchNotices();

    const onFocus = () => fetchNotices();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      pendingController?.abort();
      window.removeEventListener('focus', onFocus);
    };
  }, [isEnabled, workspaceId, refetchToken]);

  return state;
}
